import "twin.macro";
import tw, { styled } from "twin.macro";

import { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router";

import {
  IconButton,
  LinearProgress,
  ListItemButton,
  ListItemText,
  Menu,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import _ from "lodash";

import { ResourceError } from "@features/errors";
import {
  useDraftOrderSetsCount,
  useDraftOrderSetsQuery,
} from "@features/ordering";
import { formatMoneyString, upCase } from "@utility/utilityFunctions";

import { SectionTitle } from "../StyledComponents";

const Badge = styled.div([
  tw`relative flex items-center justify-center w-5 h-5 mb-1 ml-1 text-sm font-bold border-2 rounded-sm border-neutral-700 text-primary-600`,
  tw`after:(absolute w-5 h-5 top-0.5 -left-[6px] block  border-2 rounded-sm border-neutral-700 border-t-0 border-r-0)`,
]);

const DraftOrderMenuContent = ({
  osCount,
  handleClose,
}: {
  osCount: number;
  handleClose: () => void;
}) => {
  const { territories } = useSelector((state: any) => state.currentUser);

  const {
    data: orderSets,
    isFetching,
    isLoading,
    error,
  } = useDraftOrderSetsQuery({
    staleTime: 2 * 60_000,
  });

  const hasTerritories = territories ? territories.length > 1 : false;

  const groupedOs = _.groupBy(orderSets, (os) => os.territoryId);

  if (error) {
    return <ResourceError error={error} />;
  }

  return (
    <div tw="relative pb-2">
      <h3 tw="mx-3 mt-2 text-neutral-700 font-bold">Draft Orders</h3>
      {isLoading && (
        <div tw="p-4 space-y-4">
          {Array.from({ length: osCount }).map((_, i) => (
            <div key={i} tw="mb-4">
              <div tw="flex items-baseline justify-between">
                <Skeleton variant="text" width={120} height={24} />
                <Skeleton variant="text" width={60} height={20} />
              </div>
              <Skeleton variant="text" width={80} height={16} tw="mt-1" />
            </div>
          ))}
        </div>
      )}
      {Object.values(groupedOs).map((os) => (
        <div key={os[0].territoryId}>
          {hasTerritories && (
            <SectionTitle tw="mx-3 mb-1 mt-4">
              {os[0].territoryName}
            </SectionTitle>
          )}
          {os.map((os) => (
            <ListItemButton
              tw="flex gap-3 justify-between items-baseline"
              key={os.id}
              component={Link}
              to={`/order-sets/${os.id}`}
              onClick={handleClose}
            >
              <ListItemText
                tw="whitespace-normal"
                primary={
                  os.type === "pre-order"
                    ? os.programName
                    : upCase(os.type, "-")
                }
                secondary={os.orderWindowName}
              />
              <Typography>
                {os.totalPrice === 0 ? (
                  <span tw="text-neutral-400">New Order</span>
                ) : (
                  formatMoneyString(os.totalPrice)
                )}
              </Typography>
            </ListItemButton>
          ))}
        </div>
      ))}
      {hasTerritories && (
        <Typography tw="text-neutral-500 text-sm px-3 mt-3">
          * Clicking an order will switch your current territory to that order's
          territory.
        </Typography>
      )}
      {isFetching && <LinearProgress tw="absolute -bottom-2 left-0 right-0" />}
    </div>
  );
};

const DraftOrderMenu = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const osCount = useDraftOrderSetsCount();

  const handleOpen = (evt) => {
    setAnchorEl(evt.target);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    osCount > 0 && (
      <>
        <Tooltip title="Draft Orders">
          <IconButton
            {...props}
            tw="lg:(-mx-4 -mt-3)"
            aria-haspopup="true"
            onClick={(evt) => {
              handleOpen(evt);
            }}
          >
            <Badge>{osCount}</Badge>
          </IconButton>
        </Tooltip>
        <Menu
          tw="mt-3"
          disableScrollLock
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleClose}
        >
          <DraftOrderMenuContent osCount={osCount} handleClose={handleClose} />
        </Menu>
      </>
    )
  );
};

export default DraftOrderMenu;
